import { ComponentBase } from 'utils/abstract-classes/component-base'
import { isDomReady } from 'utils/dom'
import {
    ATTR_NAME,
    Header,
    Video,
    Widget,
    Subscription,
    Main,
    Driving
} from 'constants/attr-name'
import { logger } from 'utils/system/logger'
import { AbstractObserver } from 'utils/abstract-classes/abstract-observer'

type Components = Header & Video & Widget & Subscription & Main & Driving

/**
 * Creates Components by its name
 * Usage: new ComponentFactory('modal')
 * In the ATTR_NAME, there should be the key and _ selector like ATTR_NAME.modal._
 * Note: DO NOT USE OR CONTAIN VALUE IN THE _ ATTRIBUTE. IT IS USED BY THIS FACTORY.
 *
 * Camel case key will be converted as dashed lowercase in a directory name.
 * i.g. new ComponentFactory('slidingDrawer') will find components/sliding-drawer
 */
export class ComponentFactory extends AbstractObserver {
    private readonly READY = 'ts-ready'
    private elements: HTMLElement[] = []
    private attrName: string

    // Exception that does not load in this way
    // CHEET-9376 skimlinks (js solution) needs to render right away, not via observer
    // CHEET-9547 singlePlayer must render right away
    // CHEET-9792 carousel must render right away
    // CHEET-9827 priceline must render right away
    // CHEET-9852 trendingWidgetPositioner must render right away
    private exceptions = [
        'myPicks',
        'skimlinks',
        'singlePlayer',
        'carousel',
        'priceline',
        'trendingWidgetPositioner'
    ]

    /**
     * @param {ComponentNames} componentName This should be a key of ATTR_NAME,
     *                                       and this Factory automatically creates the Component
     * @param {string}         path          The second depth of ATTR_NAME.
     *                                       i.g. ATTR_NAME.header.scroll passed as ComponentFactory('header', 'scroll')
     */
    constructor(
        private componentName: keyof typeof ATTR_NAME,
        private path: keyof Components,
        private observerOptions?: IntersectionObserverInit
    ) {
        super(undefined)
        this.loadChunk().catch((e) => logger('error', e))
    }

    private async loadChunk(): Promise<void> {
        await isDomReady()

        let attrObject = (ATTR_NAME as Record<string, unknown>)[
            this.componentName.toString()
        ] as Record<string, unknown>

        if (!attrObject) {
            return
        }

        attrObject = attrObject[this.path] as Record<string, string>

        if (!attrObject) {
            return
        }

        this.attrName = attrObject._ as string

        if (!this.attrName) {
            return
        }

        this.elements = [
            ...document.querySelectorAll(
                `[${this.attrName}]:not([${this.attrName}="${this.READY}"])`
            )
        ] as HTMLElement[]

        if (!this.elements.length) {
            return
        }

        this.el = this.elements[0]

        if (!this.el) {
            return
        }

        if (this.exceptions.indexOf(this.path) !== -1) {
            this.render()
        } else {
            this.el.classList.add('ts-component')
            this.activateObserver(this.observerOptions)
        }
    }

    /**
     * When the user meets the component, load its chunk dynamically
     */
    public render(): void {
        // Load component chunk
        const pathSlug = this.toSlug(this.path.toString()) as keyof Header

        void import(`../component-chunks/${pathSlug}`).then((module) => {
            const component = module.default
            this.elements.map((element: HTMLElement) => {
                element.setAttribute(this.attrName, this.READY)
                this.toComponent(element, component)
            })
        })
    }

    /**
     * Initializes `Component` from DOM element.
     * @param {HTMLElement} element
     * @returns {ComponentBase}
     */
    private toComponent(
        element: HTMLElement,
        component: new (...param: HTMLElement[]) => ComponentBase
    ): ComponentBase {
        return new component(element)
    }

    private toSlug(str: string): string {
        return str.replace(/([A-Z])/g, '-$1').toLowerCase()
    }
}
